/**
 * UML Class Node Component
 * A compact three-compartment UML class box:
 *   1. Header — optional «stereotype», class name (+ generics)
 *   2. Attributes compartment — `<vis> name : type`
 *   3. Methods compartment    — `<vis> signature`
 *
 * Compartments are separated by thin rules (classic UML), the box sizes to its
 * content (no dead space), static members are underlined and abstract methods
 * italic. Relationships are ordinary edges whose arrow markers encode the UML
 * relation — this node owns only the box.
 */

import { memo, useEffect } from 'react'
import { NodeProps, NodeResizer, useUpdateNodeInternals } from '@xyflow/react'
import type { UMLClassContent } from '../../types/schema'
import { NodeConnectionHandles } from './NodeConnectionHandles'

interface UMLClassNodeData {
  label: string
  classContent?: UMLClassContent
  style?: {
    headerBgColor?: string
    headerTextColor?: string
    bgColor?: string
    borderColor?: string
    textColor?: string
    fontFamily?: string
  }
}

const VIS_COLOR: Record<string, string> = { '+': '#16a34a', '-': '#dc2626', '#': '#d97706', '~': '#7c3aed' }

function UMLClassNode({ id, data, selected, width, height }: NodeProps) {
  const nodeData = (data as unknown) as UMLClassNodeData
  const cc = nodeData.classContent
  const updateNodeInternals = useUpdateNodeInternals()
  useEffect(() => { updateNodeInternals(id) }, [id, updateNodeInternals])

  const headerBg = (nodeData as any).headerBgColor ?? nodeData.style?.headerBgColor ?? '#eef2ff'
  const headerText = (nodeData as any).headerTextColor ?? nodeData.style?.headerTextColor ?? '#3730a3'
  const bodyBg = (nodeData as any).bgColor ?? nodeData.style?.bgColor ?? '#ffffff'
  const textColor = (nodeData as any).textColor ?? nodeData.style?.textColor ?? '#1f2937'
  const border = (nodeData as any).borderColor ?? nodeData.style?.borderColor ?? '#c7d2fe'
  const fontFamily = nodeData.style?.fontFamily ?? 'Inter, system-ui, sans-serif'
  const mono = 'ui-monospace, SFMono-Regular, "JetBrains Mono", Menlo, monospace'

  const nodeWidth = width || 230

  if (!cc) {
    return (
      <div style={{ width: nodeWidth, minHeight: 60, background: '#fff', border: '2px solid #d1d5db', borderRadius: 6, padding: 10, fontFamily }}>
        <div style={{ fontWeight: 600 }}>{nodeData.label}</div>
        <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No class content</div>
        <NodeConnectionHandles />
      </div>
    )
  }

  const attributes = cc.attributes ?? []
  const methods = cc.methods ?? []

  // Content-fit height (no dead space): header + both compartments. Empty
  // compartments keep a slim placeholder row. An explicit resize wins.
  const ROW_H = 24
  const headerH = cc.stereotype ? 52 : 40
  const compartH = (n: number) => 10 + Math.max(n, 0.6) * ROW_H
  const contentHeight = Math.round(headerH + compartH(attributes.length) + compartH(methods.length))
  // Use the measured/resized height only when it's a real value — a transient 0
  // (pre-measure) must fall back to contentHeight or the box locks collapsed.
  const nodeHeight = typeof height === 'number' && height > 40 ? height : contentHeight

  const memberRow = (vis: string | undefined, body: React.ReactNode, opts: { italic?: boolean; underline?: boolean; key: string; title: string }) => (
    <div
      key={opts.key}
      style={{
        display: 'flex', gap: 6, alignItems: 'baseline',
        padding: '3px 10px', fontFamily: mono, fontSize: 12, lineHeight: 1.4,
        fontStyle: opts.italic ? 'italic' : 'normal',
        textDecoration: opts.underline ? 'underline' : 'none',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}
      title={opts.title}
    >
      <span style={{ color: VIS_COLOR[vis || '+'] || '#6b7280', fontWeight: 700, flexShrink: 0 }}>{vis || '+'}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{body}</span>
    </div>
  )

  return (
    <>
      {selected && <NodeResizer minWidth={160} minHeight={70} />}
      <div
        style={{
          width: nodeWidth,
          height: nodeHeight,
          background: bodyBg,
          border: `1.5px solid ${border}`,
          borderRadius: 8,
          color: textColor,
          fontFamily,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ background: headerBg, color: headerText, padding: '8px 12px', textAlign: 'center', flexShrink: 0 }}>
          {cc.stereotype && (
            <div style={{ fontSize: 10, fontStyle: 'italic', opacity: 0.85, lineHeight: 1.2 }}>«{cc.stereotype}»</div>
          )}
          <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={cc.className + (cc.generics || '')}>
            {cc.className}{cc.generics && <span style={{ opacity: 0.7, fontWeight: 500 }}>{cc.generics}</span>}
          </div>
        </div>

        {/* Attributes compartment (always present per UML, even if empty) */}
        <div style={{ borderTop: `1px solid ${border}`, padding: '5px 0', flex: 1, overflow: 'hidden', minHeight: 8 }}>
          {attributes.length === 0
            ? <div style={{ padding: '2px 10px', fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>&nbsp;</div>
            : attributes.map((a, i) => memberRow(a.visibility, (
                <>{a.name}{a.type && <span style={{ color: '#6b7280' }}>: {a.type}</span>}</>
              ), { key: 'a' + i, italic: false, underline: a.isStatic, title: `${a.visibility || '+'} ${a.name}${a.type ? ': ' + a.type : ''}` }))}
        </div>

        {/* Methods compartment */}
        <div style={{ borderTop: `1px solid ${border}`, padding: '5px 0', flexShrink: 0, overflow: 'hidden' }}>
          {methods.length === 0
            ? <div style={{ padding: '2px 10px', fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>&nbsp;</div>
            : methods.map((m, i) => memberRow(m.visibility, m.signature, { key: 'm' + i, italic: !!m.isAbstract, underline: !!m.isStatic, title: `${m.visibility || '+'} ${m.signature}` }))}
        </div>

        <NodeConnectionHandles />
      </div>
    </>
  )
}

export default memo(UMLClassNode)
